import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { StoriesFormState } from './StoriesFormState'

import React, { useState, useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleStory } from '@@partner:graphql/query/allStories/singleStory.gql'
import { useMutateSaveStory } from '@@partner:graphql/mutation/allStories/saveStories.gql'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeStoriesFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './StoriesFormState'

import {
  Form,
  Grid,
  Toggle,
  Panel,
  Row,
  Col,
  Message,
  InputGroup,
  DatePicker
} from 'rsuite'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import FormElementErrorDisplay from '@components/common/form/FormElementErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import SlidesForm from './SlidesForm'

import {
  API_URL_UPLOAD_STORY_PREVIEW_IMAGE,
  API_URL_UPLOAD_STORY_SLIDE_IMAGE
} from '@@partner:config/api'

export interface StoriesFormProps extends CrudFormProps {
  storyId?: GQL_ID
}

export default function StoriesForm ({ storyId, onSaved }: StoriesFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<StoriesFormState>(makeStoriesFormState)

  const {
    data,
    loading: loadingStories,
    error: errorLoadingStories
  } = useFetchSingleStory({
    id: storyId
  })

  const [ saveStories, { loading: loadingSaveStories } ] = useMutateSaveStory()

  useEffect(() => {
    if (!loadingStories && data) {
      setFormState((state) => mergeLeft(state, mapApiDataToState(data?.story)))
    }
  }, [ data, loadingStories, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleStoriesPreviewUploaded = useCallback(
    (preview_id, url) => {
      setFormState((state) => {
        return mergeLeftOnly([ 'preview_id', 'preview', 'url' ], state, {
          preview_id,
          preview: { url }
        })
      })
    },
    [ setFormState ]
  )

  const handleStoriesSlidesUploaded = useCallback(
    (image_id, url) => {
      setFormState((state) => {
        return {
          ...state,
          slides: state.slides.concat({ image_id, image: { url } })
        }
      })
    },
    [ setFormState ]
  )

  const handleUnlinkPreviewClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly(
        [ 'preview_id', 'preview', 'url' ],
        state,
        makeStoriesFormState()
      )
    )
  }, [ setFormState ])

  const handleUnlinkStoriesClick = useCallback(
    ({ image_id: id }) => {
      setFormState((state) => ({
        ...state,
        slides: state.slides.filter(({ image_id }) => {
          return image_id && image_id !== id
        })
      }))
    },
    [ setFormState ]
  )

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveStories({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('История сохранена')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.storyInfo)
            )

            onSaved?.(data?.storyInfo.preview_id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveStories, formState, setFormState, onSaved ]
  )

  const handleOrderSlidesChange = (index: number, slide: any) => {
    setFormState((state) => ({
      ...state,
      slides: state.slides.map((existingSlide, existingIndex) => {
        if (existingIndex === index) {
          return { ...existingSlide, ...slide }
        }
        return existingSlide
      })
    }))
  }

  if (errorLoadingStories) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить историю #${storyId} для редактирования`}
        error={errorLoadingStories}
      />
    )
  }

  const slides = formState.slides ?? []

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      fluid
    >
      <Panel className="mb-4" header="Основная информация">
        <Grid fluid>
          <Row>
            <Col md={16}>
              <Form.Group>
                <Form.ControlLabel>Название</Form.ControlLabel>
                <Form.Control name="name" placeholder="Название" />
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Позиция на сайте</Form.ControlLabel>
                <Form.Control
                  placeholder="Позиция на сайте"
                  name="sort_order"
                />
              </Form.Group>
              <Row className="mb-5">
                <Col md={5}>
                  <Form.Group>
                    <Form.ControlLabel>Активна</Form.ControlLabel>

                    <Form.Control
                      accepter={Toggle}
                      size="lg"
                      name="status"
                      checkedChildren="АКТИВНА"
                      unCheckedChildren="СКРЫТА"
                      checked={Boolean(formState.status)}
                      value={formState.status ? true : false}
                      onChange={(value) =>
                        setFormState((state) => ({
                          ...state,
                          status: value === true ? 1 : 0
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={15}>
                  <Form.ControlLabel>Период действия</Form.ControlLabel>

                  <InputGroup>
                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeFrom"
                      value={formState.activeFrom}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата начала действия"
                    />

                    <InputGroup.Addon> до </InputGroup.Addon>

                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeUntil"
                      value={formState.activeUntil}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата окончания действия"
                    />
                  </InputGroup>
                </Col>
              </Row>
              <Form.Group>
                <Form.ControlLabel>Загрузка слайдов</Form.ControlLabel>
                <FormElementErrorDisplay field="file_id" error={formError} />

                <DropzoneUploader
                  action={API_URL_UPLOAD_STORY_SLIDE_IMAGE}
                  name="story-item-image"
                  onUploaded={handleStoriesSlidesUploaded}
                  multiple={true}
                />
              </Form.Group>
            </Col>

            <Col
              md={8}
              className="text-center border rounded-md py-4 px-2 text-lg"
            >
              <Form.Group>
                <Form.ControlLabel>Превью</Form.ControlLabel>
                <FormElementErrorDisplay field="file_id" error={formError} />

                <DropzoneUploader
                  action={API_URL_UPLOAD_STORY_PREVIEW_IMAGE}
                  name="story-preview-image"
                  onUploaded={handleStoriesPreviewUploaded}
                  multiple={false}
                />
              </Form.Group>
              <Form.Group>
                {formState.preview?.url && (
                  <DropzonePreview
                    url={formState.preview.url}
                    onDelete={handleUnlinkPreviewClick}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Panel>
      <Panel className="mb-4" header="Слайды" shaded>
        <Grid fluid>
          {slides.length === 0 && (
            <Message showIcon type="warning" header="ВНИМАНИЕ">
              История не будет отображаться на сайте, если не добавлены слайды.
            </Message>
          )}

          <>
            {slides.map((slide, index) => (
              <SlidesForm
                key={`slidesForm:${index}`}
                value={slide}
                index={index}
                onChange={handleOrderSlidesChange}
                onDelete={() => handleUnlinkStoriesClick(slide)}
              />
            ))}
          </>
        </Grid>
      </Panel>
      <SaveButton
        loading={loadingStories || loadingSaveStories}
        disabled={loadingStories || loadingSaveStories}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
